// ============================================================================
// FSA TRIAL BALANCE ENDPOINTS
// ============================================================================
// Backend controller: FsaTrialBalancesController → /api/v1/FsaTrialBalances

import { buildEndpoint, buildEndpointWithParams } from '../base.endpoints';
import type { ID } from '../../types';

const BASE_PATH = '/FsaTrialBalances';

export const fsaTrialBalanceEndpoints = {
  /** GET /api/v1/FsaTrialBalances/upload-template — blank .xlsx template */
  uploadTemplate: () => buildEndpoint(`${BASE_PATH}/upload-template`),

  /** POST /api/v1/FsaTrialBalances/preview — multipart */
  preview: () => buildEndpoint(`${BASE_PATH}/preview`),

  /** POST /api/v1/FsaTrialBalances/impact-preview — multipart, no commit */
  impactPreview: () => buildEndpoint(`${BASE_PATH}/impact-preview`),

  /** POST /api/v1/FsaTrialBalances/upload — multipart */
  upload: () => buildEndpoint(`${BASE_PATH}/upload`),

  /** GET /api/v1/FsaTrialBalances/by-engagement/{engagementId} */
  listByEngagement: (engagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/by-engagement/:engagementId`, { engagementId }),

  /** GET /api/v1/FsaTrialBalances/by-engagement/{engagementId}/current */
  getCurrent: (engagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/by-engagement/:engagementId/current`, { engagementId }),

  /** GET /api/v1/FsaTrialBalances/{id} */
  getById: (id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /** GET /api/v1/FsaTrialBalances/{id}/entries */
  getEntries: (id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:id/entries`, { id }),

  /** POST /api/v1/FsaTrialBalances/{id}/finalize */
  finalize: (id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:id/finalize`, { id }),

  // FSA-2.1.6 — source registry + control accounts.

  /** GET /api/v1/FsaTrialBalances/by-engagement/{engagementId}/sources */
  listSources: (engagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/by-engagement/:engagementId/sources`, { engagementId }),

  /** GET /api/v1/FsaTrialBalances/by-engagement/{engagementId}/source-split — TB-wise grossing up */
  sourceSplit: (engagementId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/by-engagement/:engagementId/source-split`, { engagementId }),

  /** POST /api/v1/FsaTrialBalances/sources/{sourceId}/remove */
  removeSource: (sourceId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/sources/:sourceId/remove`, { sourceId }),

  /** POST /api/v1/FsaTrialBalances/{tbId}/bulk-control-account */
  bulkControlAccount: (tbId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:tbId/bulk-control-account`, { tbId }),

  /** POST /api/v1/FsaTrialBalances/{tbId}/bulk-exclude-from-sign-sweep */
  bulkExcludeFromSignSweep: (tbId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:tbId/bulk-exclude-from-sign-sweep`, { tbId }),

  /** GET /api/v1/FsaTrialBalances/{tbId}/control-account-summary */
  controlAccountSummary: (tbId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:tbId/control-account-summary`, { tbId }),

  /** FSA-2.3 — GET /api/v1/FsaTrialBalances/{id}/export-audited (consolidated + JV-adjusted rows) */
  exportAudited: (id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:id/export-audited`, { id }),

  basePath: () => BASE_PATH,
} as const;

export default fsaTrialBalanceEndpoints;
